/**
 * Frontend human-in-the-loop tool for approving or rejecting a proposed action.
 *
 * Registered via makeAssistantTool so assistant-ui:
 *   1. Sends the tool definition in RunAgentInput.tools
 *   2. Sets status to "requires-action" for human input
 *   3. Enables addResult() to send the user's decision back
 *
 * Shows a single proposed action with approve/reject buttons.
 * On reject, an optional text field lets the user explain why.
 */
import { useState } from "react";
import { makeAssistantTool } from "@assistant-ui/react";
import type { ToolCallMessagePartComponent } from "@assistant-ui/react";
import { Check, X } from "lucide-react";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import { ToolCard } from "./ToolCard";

interface ApprovalCardArgs {
  title: string;
  description: string;
  action: string;
  details: string;
}

const ApprovalCardRender: ToolCallMessagePartComponent<ApprovalCardArgs, string> = ({
  args,
  result,
  status,
  addResult,
}) => {
  const [showReason, setShowReason] = useState(false);
  const [reason, setReason] = useState("");

  const isRunning = status.type === "running";
  const isSubmitted = result != null;

  const handleApprove = () => {
    addResult(
      "The user approved this action. Proceed accordingly and respond with text only. Do NOT call any tool."
    );
  };

  const handleReject = () => {
    if (!showReason) {
      setShowReason(true);
      return;
    }
    const msg = reason.trim()
      ? `The user rejected this action. Reason: ${reason.trim()}.`
      : "The user rejected this action (no reason given).";
    addResult(`${msg} Respond with text only. Do NOT call any tool.`);
  };

  // Submitted state
  if (isSubmitted) {
    const wasApproved = typeof result === "string" && result.startsWith("The user approved");
    return (
      <ToolCard label="Approval" color="rose" isLoading={false} loadingText="" isSubmitted={true}>
        {args?.title && <p className="font-medium text-sm">{args.title}</p>}
        {args?.action && <p className="text-sm text-gray-600">{args.action}</p>}
        <p className="text-xs font-medium">
          {wasApproved ? (
            <span className="text-green-600">Approved</span>
          ) : (
            <span className="text-rose-600">Rejected</span>
          )}
        </p>
      </ToolCard>
    );
  }

  return (
    <ToolCard
      label="Approval"
      color="rose"
      isLoading={isRunning || !args}
      loadingText="Generating action..."
      isSubmitted={false}
    >
      {args?.title && <p className="font-medium text-sm">{args.title}</p>}
      {args?.description && <p className="text-xs text-muted-foreground">{args.description}</p>}

      {args?.action && (
        <div className="rounded-lg border border-rose-100 bg-rose-50/40 px-3 py-2.5">
          <p className="text-sm font-medium text-gray-800">{args.action}</p>
          {args?.details && <p className="text-xs text-muted-foreground mt-1">{args.details}</p>}
        </div>
      )}

      {showReason && (
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why are you rejecting this? (optional)"
          rows={2}
          className="w-full rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-transparent resize-none"
        />
      )}

      <div className="flex items-center gap-2 mt-1">
        <Button size="sm" onClick={handleApprove}>
          <Check className="size-3.5 mr-1" />
          Approve
        </Button>
        <Button size="sm" variant="outline" onClick={handleReject}>
          <X className="size-3.5 mr-1" />
          {showReason ? "Submit Rejection" : "Reject"}
        </Button>
      </div>
    </ToolCard>
  );
};

// Intersection with Record<string, unknown> needed because makeAssistantTool's
// generic constraint requires an index signature that interfaces don't provide.
export const ApprovalCardTool = makeAssistantTool<
  ApprovalCardArgs & Record<string, unknown>,
  string
>({
  toolName: "present_approval_card",
  type: "human",
  description:
    "Present a proposed action for the user to approve or reject. " +
    "The user can approve to proceed or reject with an optional reason.",
  parameters: {
    type: "object",
    properties: {
      title: { type: "string", description: "Title for the approval request" },
      description: {
        type: "string",
        description: "Context about why this action is being proposed",
      },
      action: {
        type: "string",
        description: "The specific action that will be taken if approved",
      },
      details: {
        type: "string",
        description: "Additional details or implications of the action",
      },
    },
    required: ["title", "action"],
  },
  render: ApprovalCardRender,
});
